import { useAuthStore } from "@/stores/useAuth";

export type FlowType = "default" | "exp_wmq";

/** URL param that opts a user into a standalone funnel (e.g. ?flow=exp_wmq). */
export const FLOW_PARAM = "flow";
export const EXP_WMQ: FlowType = "exp_wmq";

/**
 * The pricing set a flow pins, `null` when it follows the cohort draw. A
 * standalone funnel is exactly a flow that pins one: it also skips the Amplitude
 * experiments. Adding a flow is one row here plus its set
 * in `pricing.data.ts`.
 */
const FLOW_PRICING_SET_ID: Record<FlowType, string | null> = {
  default: null,
  exp_wmq: "exp_wmq",
};

/** Every flow, in table order — what the dev tools selector lists. */
export const FLOW_TYPES = Object.keys(FLOW_PRICING_SET_ID) as Array<FlowType>;

// `?? null` guards a persisted value of a flow that no longer exists (the
// store migrates those, but `FlowType` is only a compile-time promise there).
export const getFlowPricingSetId = (flow: FlowType): string | null =>
  FLOW_PRICING_SET_ID[flow] ?? null;

export const isStandaloneFlow = (flow: FlowType): boolean =>
  getFlowPricingSetId(flow) !== null;

/** Reads `?flow=` — only a standalone flow literal is a valid opt-in. */
export const parseFlowParam = (value: unknown): FlowType | null => {
  if (typeof value !== "string") return null;
  // Own keys only, never `in`: `"constructor" in {}` is true, and a tagged
  // flow is sticky. Not `Object.hasOwn` either — ES2022, so it threw here on
  // Chrome < 93 / Safari < 15.4 (SHO-746, see src/lib/polyfills).
  const flow = FLOW_TYPES.find((f) => f === value);
  return flow && isStandaloneFlow(flow) ? flow : null;
};

/**
 * Current funnel flow, read imperatively (beforeLoad, tracking init). The value
 * lives in the persisted auth store; React components use `useFunnelFlow`.
 */
export const getStoredFunnelFlow = (): FlowType =>
  useAuthStore.getState().funnelFlow;

/**
 * Decide the funnel flow at app entry and persist it. Run once in the root
 * beforeLoad, alongside UTM capture. Deterministic and race-free — it only
 * reads already-persisted state, never the async experiment fetch.
 *
 * Rules (when landing with ?flow=<standalone flow>):
 *  - already tagged standalone → stay on it (sticky, even without the param)
 *  - fresh visitor            → enter the requested flow
 */
export const resolveEntryFunnelFlow = (search: {
  [key: string]: unknown;
}): void => {
  const auth = useAuthStore.getState();
  if (isStandaloneFlow(auth.funnelFlow)) return;

  const requested = parseFlowParam(search[FLOW_PARAM]);
  if (requested === null) return;

  useAuthStore.setState({ funnelFlow: requested });
};
